import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { listVacancyActions } from '@mkp/vacancy/state/actions';
import * as actions from '@mkp/company/state/actions';
import { MessageError, isMessageError } from '@mkp/shared/data-access';
import { CompanyResource } from '@mkp/company/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { selectCompanyByVacancy } from './selectors';

@Injectable()
export class CompanyProfileEffects {
  private readonly actions$ = inject(Actions);
  private readonly companyResource = inject(CompanyResource);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  companyGetCompaniesProfileList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.CompanyGetProfileList),
        switchMap(({ query }) => {
          const queryOptions = query || { limit: 25 };
          return this.companyResource.getCompanies(queryOptions).pipe(
            map(({ companyProfiles, totalCount, filter, _links }) =>
              actions.CompanyGetProfileListSuccess({
                companyProfiles,
                totalCount,
                filter,
                _links,
              })
            ),
            catchError((error: unknown) =>
              isCompanyProfileError(error)
                ? of(actions.CompanyGetProfileListFailure({ error }))
                : EMPTY
            )
          );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  readonly navigateToCompanyFromVacancy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(listVacancyActions.navigateToCompanyFromVacancy),
        switchMap(({ vacancy }) =>
          this.store.select(selectCompanyByVacancy(vacancy)).pipe(filter(Boolean))
        ),
        tap((company) => this.router.navigate([`/${AppConfig.routes.company}/edit/${company.id}`]))
      ),
    { dispatch: false }
  );
}

const isCompanyProfileError = (error: unknown): error is MessageError =>
  isMessageError(error, 'CompanyProfileEffects');
